/**
 * The documents as a tree, derived rather than transported.
 *
 * The client sends a flat list in which every entry already says which directory it is
 * listed under. That is enough to reconstruct the whole shape here, so navigating one
 * level deeper costs nothing over the relay — and the relay is the slow, plaintext,
 * crosses-our-infrastructure part of this product (`DEC-11`). One request per folder would
 * be one more thing on the wire for every click.
 *
 * **Lanes are a facet, folders are a place, and the tree has to serve both.** A lane's
 * first level is therefore its documents grouped by the directory they sit in below
 * `docs/`; from there down it is ordinary directory navigation, because once you are inside
 * `docs/decisions` everything in it is already the thing you asked for.
 */
import { isAboveSections, isDocument, type DocEntry } from "./docs"

export const DOC_ROOT = "docs"
/** The section for documents the repository files nowhere. Not a lane; a leftover. */
export const UNFILED = "unfiled"

export interface Node {
  /** A directory path for a section, a file path for a document. */
  path: string
  dir: boolean
  title: string
  summary: string
  /** `folder` · `document` · `table` · `board` — what the card should look like. */
  kind: string
  /** The board template when this node is a board, so it opens as columns not a list. */
  board?: string
  count: number
  /** The thing's own frontmatter, so a card can show what a table column used to. */
  meta?: Record<string, string>
  entry?: DocEntry
}

const dirname = (p: string) => p.slice(0, Math.max(0, p.lastIndexOf("/")))

/** The directory an entry *is*, as opposed to the one it is listed under. */
export function ownDir(e: DocEntry): string | null {
  return e.kind === "folder" ? dirname(e.path) : null
}

export function boardOf(e: DocEntry): string | undefined {
  const t = e.meta?.board?.trim()
  return t || undefined
}

/** True for the `index.md` of a card on a board — a state, not a document to browse to. */
export function isBoardItem(e: DocEntry): boolean {
  return !!e.meta?.item
}

/**
 * What is directly inside `dir`.
 *
 * Board items are excluded everywhere. A card is not a document somebody navigates to by
 * folder; it is a position in a column, and listing it as a file would show the same thing
 * twice under two different rules for what it is.
 */
export function childrenOf(entries: DocEntry[], dir: string): Node[] {
  const out: Node[] = []
  for (const e of entries) {
    if (e.folder !== dir || isBoardItem(e)) continue
    const own = ownDir(e)
    out.push(own
      ? section(entries, own, e)
      : { path: e.path, dir: false, title: e.title, summary: e.summary ?? "",
          kind: e.kind, count: 0, meta: e.meta, entry: e })
  }
  return out.sort(order)
}

function section(entries: DocEntry[], dir: string, index: DocEntry): Node {
  const board = boardOf(index)
  return {
    path: dir,
    dir: true,
    title: index.title,
    summary: index.summary ?? "",
    kind: board ? "board" : "folder",
    board,
    count: board ? entries.filter(e => isBoardItem(e) && e.folder === dir).length
                 : childrenOf(entries, dir).length,
  }
}

/**
 * A lane's first level.
 *
 * Documents in one lane are scattered on purpose — a pricing decision and a pricing
 * research note live in different directories by construction — so this groups them by
 * where they are rather than pretending the lane is a directory. A directory that has an
 * `index.md` keeps the title and description written in it; one that does not is named
 * after itself.
 */
export function laneNodes(entries: DocEntry[], lane: string, known?: Set<string>): Node[] {
  // `unfiled` is a section with no lane behind it: it holds whatever the repository does
  // not file. Matching it by name would match nothing, which is how it came to be counted
  // in the menu and empty on the page — present, numbered, and unreachable.
  const mine = entries.filter(e => !isBoardItem(e) && isDocument(e.path) && (lane === UNFILED
    // Not "has no lane" but "has no lane *and* is not one of the things filed above them" —
    // the same rule `categories` counts by, so the number and the page agree.
    ? (!e.lane || !(known?.has(e.lane) ?? true)) && !isAboveSections(e.path)
    : e.lane === lane))
  const home = `${DOC_ROOT}/${lane}`
  const groups = new Map<string, Node>()
  const loose: Node[] = []

  for (const e of mine) {
    // The lane's own directory is transparent. A board created in `docs/product/` is one of
    // the product lane's things, not a folder called Product containing one thing — and
    // showing it as the latter buries every new section one level below where it was made.
    const base = e.path.startsWith(home + "/") ? home : DOC_ROOT
    const own = ownDir(e)
    const dir = own ?? dirname(e.path)

    if (dir === base) {
      // A folder whose own directory *is* the base is the lane's own page — its title and
      // description. Listing it here would make the section a card inside itself.
      if (!own) loose.push({ path: e.path, dir: false, title: e.title,
                             summary: e.summary ?? "", kind: e.kind, count: 0,
                             meta: e.meta, entry: e })
      continue
    }
    const top = dir.slice(0, dir.indexOf("/", base.length + 1) + 1 || undefined)
      .replace(/\/$/, "")
    if (!groups.has(top)) {
      const index = entries.find(x => ownDir(x) === top)
      groups.set(top, index ? section(entries, top, index) : {
        path: top, dir: true, title: prettify(top.split("/").pop() ?? top),
        summary: "", kind: "folder", count: 0,
      })
    }
  }

  // Counted over the lane, not over the directory: a section shown inside a lane should
  // say how much of *this* lane is in it, or the number describes a page you did not open.
  for (const [dir, node] of groups) {
    if (node.kind === "board") continue
    node.count = mine.filter(e => e.path.startsWith(dir + "/") && ownDir(e) !== dir).length
  }
  return [...groups.values(), ...loose].sort(order)
}

/** Sections first, then by name: a list that mixes them reads as unsorted. */
function order(a: Node, b: Node): number {
  if (a.dir !== b.dir) return a.dir ? -1 : 1
  return a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: "base" })
}

/** The page a section shows above its cards, written in the section's own `index.md`. */
export function describeDir(entries: DocEntry[], dir: string):
    { title: string; summary: string; entry?: DocEntry } {
  const index = entries.find(e => ownDir(e) === dir)
  if (index) return { title: index.title, summary: index.summary ?? "", entry: index }
  return { title: prettify(dir.split("/").pop() ?? dir), summary: "" }
}

/** Every board in the project, for the one question that spans them: where to promote to. */
export function boards(entries: DocEntry[]): Array<{ path: string; title: string; template: string }> {
  return entries
    .filter(e => boardOf(e) && boardOf(e) !== "ideas")
    .map(e => ({ path: e.path, title: e.title, template: boardOf(e)! }))
}

export function prettify(name: string): string {
  const s = name.replace(/[-_]+/g, " ").trim()
  return s ? s[0].toUpperCase() + s.slice(1) : name
}

/** `docs/product/board` -> the crumbs above it, so drilling in is not a one-way trip. */
export function crumbs(dir: string): string[] {
  const parts = dir.split("/").filter(Boolean)
  return parts.slice(1).map((_, i) => parts.slice(0, i + 2).join("/"))
}
